import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';


import { useNavigate, useLocation } from 'react-router-dom';
import { FirmSelectorCompact } from './FirmSelector';
import { CreditBadgeCompact } from './CreditBadge';
import { UserProfileDialog } from './UserProfileDialog';
import { useAuth } from "../../contexts/AuthContext";
import { UserRole } from '@taxassist/api-client';
import { getRoleDisplayName } from '../../utils/userPermissions';
import { FirmDetailsDrawer } from '../admin/FirmDetailsDrawer';
import { useListAccessibleFirms } from '@taxassist/api-client';


export function AppHeader({
  onHamburgerMenuToggle,
}: {
  onHamburgerMenuToggle: () => void;
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [firmDrawerOpen, setFirmDrawerOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const open = Boolean(anchorEl);
  
  // Check if user is currently in the Ops section - hide firm selector there
  const isInOpsSection = location.pathname.startsWith('/ops');
  
  // Get page title based on current route
  const getPageTitle = () => {
    if (location.pathname.startsWith('/ops')) {
      return 'Reviewer Workspace';
    }
    if (location.pathname.startsWith('/admin')) {
      if (location.pathname.includes('/firms')) {
        return 'Firm Management';
      }
      return 'Administration';
    }
    if (location.pathname.startsWith('/profile')) {
      return 'Profile';
    }
    return '';
  };

  // Get accessible firms for firm_admin users
  const { data: firmsData } = useListAccessibleFirms(
    undefined, // no pagination params needed
    {
      query: {
        enabled: user?.role === UserRole.firm_admin,
        queryKey: ['accessible-firms'],
      }
    }
  );
  const firms = firmsData?.firms || [];
  const currentFirm = firms.length > 0 ? firms[0] : null; // Firm admin should only have 1 firm

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleManageFirm = () => {
    handleUserMenuClose();
    if (currentFirm) {
      setFirmDrawerOpen(true);
    }
  };

  const handleProfileClick = () => {
    handleUserMenuClose();
    setProfileDialogOpen(true);
  };

  const getDisplayName = () => {
    if (user?.given_name && user?.family_name) {
      return `${user.given_name} ${user.family_name}`;
    }
    return user?.email || 'User';
  };

  const getInitials = () => {
    if (user?.given_name && user?.family_name) {
      return `${user.given_name[0]}${user.family_name[0]}`.toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  };

  return (
    <AppBar position="static"
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderBottom: 1,
        borderColor: 'divider',
        boxShadow: 'none',
      }}
    >
      <Toolbar sx={{ minHeight: '64px !important' }}>
        {isMobile && (
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={onHamburgerMenuToggle}
          >
            <MenuIcon sx={{color: 'action.active'}} />
          </IconButton>
        )}

        {/* Logo/Brand - Shows page title in ops/admin sections */}
        <Box display="flex" alignItems="center" sx={{ flexGrow: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            {getPageTitle()}
          </Typography>
        </Box>

        {/* Firm Selector and Credit Badge - Hide when in Ops section (firm-agnostic) */}
        {!isInOpsSection && (
          <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <FirmSelectorCompact />
            <CreditBadgeCompact />
          </Box>
        )}

        {/* User Menu */}
        {user && (
          <Box display="flex" alignItems="center">
            <IconButton
              onClick={handleUserMenuClick}
              sx={{ p: 0.5 }}
              aria-controls={open ? 'user-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
            >
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                {getInitials()}
              </Avatar>
            </IconButton>

            <Menu
              id="user-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleUserMenuClose}
              onClick={handleUserMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              sx={{
                '& .MuiPaper-root': {
                  minWidth: 220,
                  mt: 1,
                }
              }}
            >
              {/* User Info */}
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {getDisplayName()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  {user.email}
                </Typography>
                {user.role && (
                  <Typography variant="caption" color="text.secondary" sx={{ 
                    textTransform: 'capitalize',
                    backgroundColor: 'action.hover',
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    display: 'inline-block',
                    mt: 0.5
                  }}>
                    {getRoleDisplayName(user.role as UserRole)}
                  </Typography>
                )}
                {user.groups && user.groups.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ 
                    fontSize: '0.7rem',
                    opacity: 0.7,
                    display: 'block',
                    mt: 0.25
                  }}>
                    Groups: {user.groups.join(', ')}
                  </Typography>
                )}
              </Box>

              <Divider />

              {/* Profile Link */}
              <MenuItem onClick={handleProfileClick}>
                <PersonIcon sx={{ mr: 1.5, fontSize: '1.25rem' }} />
                Profile
              </MenuItem>

              {/* Manage Firm - Only for firm_admin users */}
              {user?.role === UserRole.firm_admin && currentFirm && (
                <MenuItem onClick={handleManageFirm}>
                  <BusinessIcon sx={{ mr: 1.5, fontSize: '1.25rem' }} />
                  Manage Firm
                </MenuItem>
              )}

              <Divider />

              {/* Logout */}
              <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                <LogoutIcon sx={{ mr: 1.5, fontSize: '1.25rem' }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>

      {/* Firm Details Drawer for firm_admin users */}
      {currentFirm && (
        <FirmDetailsDrawer
          open={firmDrawerOpen}
          onClose={() => setFirmDrawerOpen(false)}
          firm={currentFirm}
        />
      )}

      {/* User Profile Dialog */}
      <UserProfileDialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
      />
    </AppBar>
  );
}
